'use client';

import { useMemo } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha, Box, Typography } from '@mui/material';

import { findResolvedOutcome, getEventType } from '@/utils';
import type { CardVariantProps } from './types';

/**
 * ResolvedMarketCard Component
 * Displays the winning outcome of a resolved market.
 * The winning outcome is identified by having a price of "1".
 */
export function ResolvedMarketCard({ market, event }: CardVariantProps) {
  const winner = useMemo(() => findResolvedOutcome(event, market), [event, market]);
  const eventType = useMemo(() => getEventType(event), [event]);

  if (!winner) {
    return (
      <Box sx={{ height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Not yet resolved
        </Typography>
      </Box>
    );
  }

  // Force "Yes" or "No" for binary markets to improve clarity
  const rawWinnerName = (winner as any).name || '';
  const isBinary = eventType === 'SIMPLE_BINARY' || eventType === 'CUSTOM_BINARY';
  const winnerName =
    isBinary && (rawWinnerName.toLowerCase() === 'yes' || rawWinnerName.toLowerCase() === 'no')
      ? rawWinnerName
      : (winner as any).name || rawWinnerName || 'Unknown';

  const isNo = winnerName.toLowerCase() === 'no';

  // Design matching: "No" usually shows red treatment with Cancel icon,
  // others show green with CheckCircle icon
  const color = isNo ? 'error.main' : 'success.main';
  const Icon = isNo ? CancelIcon : CheckCircleIcon;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          py: 1.5,
          px: 2,
          borderRadius: 2,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.text.primary, 0.04)
              : alpha(theme.palette.text.primary, 0.02),
          border: '1px solid',
          borderColor: 'divider',
          width: '100%',
          transition: 'all 0.2s ease',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1rem',
          }}
        >
          {winnerName}
          <Icon sx={{ color, fontSize: 20 }} />
        </Typography>
      </Box>
    </Box>
  );
}
